import Image from "next/image";
import Link from "next/link";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { Empty, Heading, ImageWrapper, Spinner, Text } from "ui-components";

import NewsLetter from "@/components/newsletter/NewsLetter";
import { TrainingItem } from "@/src/app/[locale]/(private)/profile/[tab]/components/resource-center/TrainingItem";
import { NEWSLETTER_ARCHIVE_THUMBNAIL } from "@/src/constants/images";
import {
  NewsletterArchiveListResponse,
  useOrganizationSharedFileGetFolders,
  useOrganizationSharedFileGetNewsletterArchive,
} from "@/src/hooks/api/generated";
import { isRecentlyUpdated, slugify } from "@/src/utils/resourceUtils";
import { formateDateFromUTC } from "@/utils/formateDate";

import NewsletterModal from "../../newsletter-archives/NewsletterModal";

import { ResourceCategoryItem } from "./ResourceCategoryItem";

const asset_url =
  "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/resource-center";

export function ResourceList(): JSX.Element {
  const { data: documentsData, isLoading: isLoadingDocuments } =
    useOrganizationSharedFileGetFolders();
  const [newsletterData, setNewsLetterData] =
    useState<NewsletterArchiveListResponse>();
  const { mutate: fetchNewsLetter } =
    useOrganizationSharedFileGetNewsletterArchive();
  const params = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = params.locale as string;
  const tab = params.tab as string;
  const fileId = searchParams.get("fileId");
  const source = searchParams.get("source");

  useEffect(() => {
    if (fileId && documentsData) {
      const folder = documentsData.find((f) =>
        f.files.some((file) => file.id.toString() === fileId)
      );

      if (folder) {
        const url = `/${locale}/profile/${tab}/${slugify(
          folder.file_name
        )}?fileId=${fileId}${source ? `&source=${source}` : ""}`;
        router.push(url);
      }
    }
  }, [fileId, documentsData, router, locale, tab, source]);

  const [selectedNewsletterId, setSelectedNewsletterId] = useState<
    number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (id: number): void => {
    setSelectedNewsletterId(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setSelectedNewsletterId(null);
  };

  useEffect(() => {
    fetchNewsLetter(
      {
        data: {
          page: 1,
          pageSize: 4,
        },
      },
      {
        onSuccess: (data) => {
          setNewsLetterData(data);
        },
      }
    );
  }, [fetchNewsLetter]);

  if (isLoadingDocuments) {
    return (
      <div className="flex flex-1 flex-grow item-center justify-center">
        <Spinner intent="brand-primary" />
      </div>
    );
  }

  const trainingItems = {
    name: "Training",
  };
  if (documentsData && documentsData.length === 0) {
    return (
      <div className="flex flex-1 flex-grow item-center justify-center">
        <Empty text="No documents found." />
      </div>
    );
  }
  return (
    <>
      <Heading variant="h2">Browse by Category</Heading>
      <Text variant="small">
        Explore our organized collection of resources by category to find
        exactly what you need.
      </Text>
      <div className="flex flex-col mt-3 gap-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 justify-items-center">
          <Link
            key="training-12"
            href={`/profile/${tab}/${slugify("training")}`}
            className="w-full"
          >
            <TrainingItem category={trainingItems} />
          </Link>
          {documentsData?.map((folder) => (
            <Link
              key={folder.id}
              href={`/profile/${tab}/${slugify(folder.file_name)}`}
              className="w-full"
            >
              <ResourceCategoryItem category={folder} />
            </Link>
          ))}
        </div>
      </div>
      <div className="flex flex-col md:flex-row h-full w-full items-start md:items-center justify-start gap-8 my-8">
        <div className="flex w-full md:flex-1 flex-col items-start justify-start gap-2">
          <div className="flex self-stretch flex-col items-start justify-start">
            <div className="flex flex-col justify-center text-[24px] font-bold text-black">
              Newsletter Archives
            </div>
          </div>

          <div className="flex flex-col justify-center text-[14px] font-medium leading-5 text-black">
            Access our complete collection of newsletters, reports, and regular
            communications.
          </div>
        </div>
        <div className="self-center md:self-auto">
          <NewsLetter />
        </div>
      </div>
      {newsletterData?.data && newsletterData.data.length > 0 && (
        <div className="flex flex-col lg:flex-row h-full w-full items-start justify-start gap-8">
          <div className="flex flex-col items-start justify-start gap-4 w-full lg:w-1/2">
            <div className="flex flex-col justify-center text-[20px] font-bold text-[#0A0A0A]">
              Latest Edition
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => handleOpenModal(newsletterData.data![0]!.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpenModal(newsletterData.data![0]!.id);
                }
              }}
              className="flex w-full flex-col items-start justify-start gap-4 overflow-hidden rounded-lg outline outline-1 outline-offset-[-1px] outline-[#EAEAEA] pb-4 cursor-pointer"
            >
              <ImageWrapper
                src={
                  newsletterData.data[0]?.thumbnail ||
                  NEWSLETTER_ARCHIVE_THUMBNAIL
                }
                width={620}
                height={288}
                alt={newsletterData.data[0]?.name || ""}
              />
              <div className="flex w-full items-start justify-start gap-2 px-4">
                <div className="flex flex-1 flex-col items-start justify-start gap-2">
                  <div className="w-full text-[14px] font-medium text-[#535455]">
                    {newsletterData.data[0]?.publishedAt
                      ? formateDateFromUTC(
                          newsletterData.data[0]?.publishedAt,
                          "MMMM D, YYYY"
                        )
                      : ""}
                  </div>

                  <div className="flex flex-col justify-center w-full text-[18px] font-semibold text-black break-words">
                    {newsletterData.data[0]?.name}
                  </div>
                </div>
                <div className="relative h-6 w-6 shrink-0 mt-2">
                  {newsletterData.data[0]?.publishedAt &&
                    isRecentlyUpdated(newsletterData.data[0]?.publishedAt) && (
                      <Image
                        className="w-6 h-6 max-w-[24px]"
                        src={`${asset_url}/dotzakytv.gif`}
                        height={24}
                        width={24}
                        alt="Resource Center"
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col items-start justify-start gap-4 w-full lg:w-1/2">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col justify-center text-[20px] font-bold text-[#0A0A0A]">
                Archives
              </div>

              <div className="flex flex-col justify-center text-[14px] font-medium leading-5 text-black underline">
                <Link href={`/${locale}/profile/${tab}/newsletter-archives`}>
                  View all
                </Link>
              </div>
            </div>

            {newsletterData.data.slice(1, 4).map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenModal(item.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenModal(item.id);
                  }
                }}
                className="flex flex-col sm:flex-row w-full items-start sm:items-center justify-start gap-4 sm:gap-6 overflow-hidden rounded-lg outline outline-1 outline-offset-[-1px] outline-[#EAEAEA] pb-4 sm:pb-0 sm:pr-4 cursor-pointer h-auto sm:h-[130px]"
              >
                <ImageWrapper
                  src={item.thumbnail || NEWSLETTER_ARCHIVE_THUMBNAIL}
                  width={280}
                  height={220}
                  alt={item.name || ""}
                  className="w-full sm:w-[280px]"
                />
                <div className="flex flex-1 flex-col items-start justify-start gap-2 py-2 px-4 sm:px-0">
                  <div className="w-full text-[14px] font-medium text-[#535455]">
                    {item.publishedAt
                      ? formateDateFromUTC(item.publishedAt, "MMMM D, YYYY")
                      : ""}
                  </div>
                  <div className="flex flex-col justify-center w-full text-[16px] sm:text-[18px] font-semibold text-black break-words">
                    {item.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedNewsletterId && (
        <NewsletterModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          newsletterId={selectedNewsletterId}
        />
      )}
    </>
  );
}
